/**
 * NOVA CHARTER — input router.
 *
 * One object owns the whole input layer: it runs the gamepad scanner and the
 * keyboard scanner, decides which *source* (a pad index or a keyboard scheme)
 * owns which *seat* (`PlayerId` 0..3), and emits a single normalised
 * `InputEvent` stream for the UI and engine.
 *
 * Seat ownership rules
 *  - Sources start **unbound**. In the lobby, `setJoinEnabled(true)` lets the
 *    first non-directional button press from an unbound source claim the lowest
 *    free seat ("press A to join"). Directional presses never claim, so a
 *    player can nudge a stick while reading the screen without joining.
 *  - A pad that drops keeps its seat (shown as `connected: false`) and reclaims
 *    it on reconnect, matched on remembered `id` + `index`, so unplugging a flat
 *    controller mid-match never reshuffles the table.
 *  - Outside the lobby an unbound source is silent, with two deliberate
 *    exceptions documented on `resolveSeat()` so the game can never end up with
 *    an un-navigable screen.
 *
 * `seat: null` is therefore only ever emitted while join is enabled.
 */

import type { InputButton, InputEvent, InputSink, PlayerId } from '../game/types';
import { ALL_SEATS } from '../game/constants';
import { GamepadManager } from './gamepad';
import { KeyboardSource, SCHEME_COUNT, keyboardSchemeLabel } from './keyboard';

type Edge = InputEvent['edge'];
type SourceKind = 'gamepad' | 'keyboard';

/* ------------------------------------------------------------------- public */

export interface SeatBinding {
  seat: PlayerId;
  source: { kind: 'gamepad'; index: number } | { kind: 'keyboard'; scheme: number } | null;
  /** Human-readable, e.g. "Xbox Wireless Controller" or "Keyboard (WASD)". */
  label: string;
  connected: boolean;
}

/** Analog snapshot handed back by `InputRouter.axes()`. */
export interface SeatAxes {
  lx: number;
  ly: number;
  rx: number;
  ry: number;
  lt: number;
  rt: number;
}

export interface InputRouterOptions {
  /** Called for every normalised input event. */
  onEvent: InputSink;
  /** Called when the set of bindings changes (join, drop, reconnect). */
  onBindingsChanged?: (bindings: SeatBinding[]) => void;
  /** Called on the first ever input of any kind — used to unlock audio. */
  onFirstInput?: () => void;
}

/** Shown for a seat with nothing bound to it. */
export const OPEN_SEAT_LABEL = 'Open';

/* ------------------------------------------------------------------ private */

interface SeatSlot {
  readonly seat: PlayerId;
  kind: SourceKind | null;
  /** Gamepad index or keyboard scheme; -1 when unbound. */
  slot: number;
  /** Remembered `Gamepad.id`, for deterministic reconnect. */
  padId: string;
  label: string;
  connected: boolean;
  /** Frozen, reused so every emitted event shares one source object. */
  source: InputEvent['source'] | null;
  /** Reused analog snapshot — `axes()` never allocates. */
  axes: SeatAxes;
}

const DIRECTION_BUTTONS: readonly InputButton[] = ['up', 'down', 'left', 'right'];

function isDirection(button: InputButton): boolean {
  for (let i = 0; i < DIRECTION_BUTTONS.length; i++) {
    if (DIRECTION_BUTTONS[i] === button) return true;
  }
  return false;
}

function zeroAxes(a: SeatAxes): void {
  a.lx = 0;
  a.ly = 0;
  a.rx = 0;
  a.ry = 0;
  a.lt = 0;
  a.rt = 0;
}

/* ------------------------------------------------------------------ router */

export class InputRouter {
  private readonly opts: InputRouterOptions;
  private readonly gamepads: GamepadManager;
  private readonly keyboard: KeyboardSource;
  private readonly seats: SeatSlot[];

  /** Cached frozen `source` objects, keyed by slot. */
  private readonly padSources: (InputEvent['source'] | null)[] = [];
  private readonly keySources: (InputEvent['source'] | null)[] = [];

  private joinEnabled = false;
  private started = false;
  private firstInputSeen = false;
  /** Hotseat baton: which seat unbound keyboard input falls through to. */
  private activeSeat: PlayerId | null = null;

  constructor(opts: InputRouterOptions) {
    this.opts = opts;
    this.seats = ALL_SEATS.map((seat) => ({
      seat,
      kind: null,
      slot: -1,
      padId: '',
      label: OPEN_SEAT_LABEL,
      connected: false,
      source: null,
      axes: { lx: 0, ly: 0, rx: 0, ry: 0, lt: 0, rt: 0 },
    }));

    this.gamepads = new GamepadManager({
      onEvent: (index, button, edge, value) => {
        this.dispatch('gamepad', index, button, edge, value);
      },
      onConnect: (index, id, label) => {
        this.handlePadConnect(index, id, label);
      },
      onDisconnect: (index) => {
        this.handlePadDisconnect(index);
      },
    });

    this.keyboard = new KeyboardSource({
      onEvent: (scheme, button, edge, value) => {
        this.dispatch('keyboard', scheme, button, edge, value);
      },
    });
  }

  /* --------------------------------------------------------- lifecycle */

  start(): void {
    if (this.started) return;
    this.started = true;
    this.gamepads.start();
    this.keyboard.start();
    // Pads may already have been detected by start()'s rescan.
    this.notifyBindings();
  }

  stop(): void {
    if (!this.started) return;
    this.started = false;
    this.gamepads.stop();
    this.keyboard.stop();
  }

  dispose(): void {
    this.stop();
    this.gamepads.dispose();
    this.keyboard.dispose();
    this.padSources.length = 0;
    this.keySources.length = 0;
  }

  /** Poll gamepads. Call once per animation frame from the game loop. */
  poll(nowMs: number): void {
    this.gamepads.poll(nowMs);
    // Same timestamp for both, so keyboard and pad repeat in lockstep.
    this.keyboard.poll(nowMs);
  }

  /* ------------------------------------------------------------ bindings */

  get bindings(): SeatBinding[] {
    const out: SeatBinding[] = [];
    for (let i = 0; i < this.seats.length; i++) {
      const s = this.seats[i];
      out.push({
        seat: s.seat,
        source: describeSource(s.kind, s.slot),
        label: s.label,
        connected: s.connected,
      });
    }
    return out;
  }

  /** Lobby: allow unbound sources to claim a free seat on next button press. */
  setJoinEnabled(enabled: boolean): void {
    this.joinEnabled = enabled;
  }

  /**
   * Hotseat baton. Global keys (Escape/Enter/Space) and unbound keyboard input
   * fall through to this seat, so a single shared keyboard can drive whoever is
   * currently acting. Pass `null` to disable the fallback.
   */
  setActiveSeat(seat: PlayerId | null): void {
    this.activeSeat = seat;
    if (seat === null) return;
    const s = this.seats[seat];
    if (s.kind === 'keyboard') this.keyboard.setActiveScheme(s.slot);
  }

  /** Force a binding (used by the lobby UI / seat swapping). */
  assignSeat(seat: PlayerId, source: SeatBinding['source']): void {
    if (source === null) {
      this.releaseSeat(seat);
      return;
    }
    const kind: SourceKind = source.kind;
    const slot = source.kind === 'gamepad' ? source.index : source.scheme;
    const target = this.seats[seat];
    const holder = this.findSeatBySource(kind, slot);

    if (holder === target) return;

    if (holder) {
      // The source already sits somewhere: swap the two seats so the lobby's
      // "move me to seat 3" never silently unseats a player.
      const prevKind = target.kind;
      const prevSlot = target.slot;
      this.bind(target, kind, slot);
      this.bind(holder, prevKind, prevSlot);
    } else {
      this.bind(target, kind, slot);
    }
    this.notifyBindings();
  }

  releaseSeat(seat: PlayerId): void {
    const s = this.seats[seat];
    if (s.kind === null) return;
    this.bind(s, null, -1);
    this.notifyBindings();
  }

  /** True if any gamepad is currently connected. */
  get hasGamepad(): boolean {
    return this.gamepads.count > 0;
  }

  /** Fire a short rumble on a seat's controller if supported. */
  rumble(seat: PlayerId, strength = 0.6, durationMs = 180): void {
    const s = this.seats[seat];
    if (s.kind !== 'gamepad' || !s.connected) return;
    this.gamepads.rumble(s.slot, strength, durationMs);
  }

  /**
   * Snapshot of a seat's current analog state, for smooth camera/aim.
   *
   * The returned object is reused per seat (so this is allocation-free in a
   * per-frame loop) — copy the fields if you need to keep them.
   */
  axes(seat: PlayerId): SeatAxes {
    const s = this.seats[seat];
    const out = s.axes;
    if (s.kind === 'gamepad') {
      if (this.gamepads.writeAxes(s.slot, out)) return out;
    } else if (s.kind === 'keyboard') {
      if (this.keyboard.writeAxes(s.slot, out)) return out;
    }
    zeroAxes(out);
    return out;
  }

  /* ------------------------------------------------------ event plumbing */

  private dispatch(
    kind: SourceKind,
    slot: number,
    button: InputButton,
    edge: Edge,
    value: number,
  ): void {
    if (!this.firstInputSeen) {
      this.firstInputSeen = true;
      // NB: gamepad polling is not a "user gesture" in Chrome, so an audio
      // context unlocked from here may stay suspended until a key or click.
      this.opts.onFirstInput?.();
    }

    const seat = this.resolveSeat(kind, slot, button, edge);
    if (seat === undefined) return; // dropped: unbound source, join closed

    this.opts.onEvent({
      seat,
      source: this.sourceFor(kind, slot),
      button,
      edge,
      value,
    });
  }

  /**
   * Seat for an event, or `undefined` to drop it.
   *
   * Order of preference:
   *  1. The source's own seat.
   *  2. Lobby join: a fresh `down` on a non-directional button claims the
   *     lowest free seat. If every seat is taken the event still goes out with
   *     `seat: null` so the lobby can say "no free seats".
   *  3. Hotseat fallback: unbound *keyboard* input follows `setActiveSeat()`,
   *     which is how one shared keyboard drives the acting player.
   *  4. Nothing is bound at all: route to seat 0, so a title screen reached
   *     before anybody has joined is still navigable.
   *  5. Otherwise drop — a spectator pad must not puppet a seated player.
   */
  private resolveSeat(
    kind: SourceKind,
    slot: number,
    button: InputButton,
    edge: Edge,
  ): PlayerId | null | undefined {
    const owner = this.findSeatBySource(kind, slot);
    if (owner) return owner.seat;

    if (this.joinEnabled) {
      if (edge === 'down' && !isDirection(button)) {
        const claimed = this.claimSeat(kind, slot);
        if (claimed !== null) return claimed;
      }
      return null;
    }

    if (kind === 'keyboard' && this.activeSeat !== null) return this.activeSeat;
    if (!this.anyBound()) return 0;
    return undefined;
  }

  /** Lowest free seat, or `null` when the table is full. */
  private claimSeat(kind: SourceKind, slot: number): PlayerId | null {
    for (let i = 0; i < this.seats.length; i++) {
      const s = this.seats[i];
      if (s.kind !== null) continue;
      this.bind(s, kind, slot);
      this.notifyBindings();
      return s.seat;
    }
    return null;
  }

  private anyBound(): boolean {
    for (let i = 0; i < this.seats.length; i++) {
      if (this.seats[i].kind !== null) return true;
    }
    return false;
  }

  private findSeatBySource(kind: SourceKind, slot: number): SeatSlot | null {
    for (let i = 0; i < this.seats.length; i++) {
      const s = this.seats[i];
      if (s.kind === kind && s.slot === slot) return s;
    }
    return null;
  }

  /** Point a seat at a source (or at nothing) and refresh its display fields. */
  private bind(s: SeatSlot, kind: SourceKind | null, slot: number): void {
    s.kind = kind;
    s.slot = kind === null ? -1 : slot;
    if (kind === 'gamepad') {
      // `idOf` keeps answering after a disconnect (pad state doubles as
      // reconnect memory), so this stays correct for an away controller.
      s.padId = this.gamepads.idOf(slot);
      s.label = this.gamepads.label(slot);
      s.connected = this.gamepads.isConnected(slot);
      s.source = this.sourceFor('gamepad', slot);
    } else if (kind === 'keyboard') {
      s.padId = '';
      s.label = keyboardSchemeLabel(slot);
      s.connected = true; // a keyboard is always "plugged in"
      s.source = this.sourceFor('keyboard', slot);
    } else {
      s.padId = '';
      s.label = OPEN_SEAT_LABEL;
      s.connected = false;
      s.source = null;
    }
    zeroAxes(s.axes);
  }

  /* ------------------------------------------------- gamepad connections */

  /**
   * Deterministic reconnect. A pad coming back reclaims the seat it left,
   * matched in order of confidence: same id *and* port, then same id on a
   * different port (Chrome renumbers when other pads drop), then same port with
   * a different id (a dead controller swapped for a fresh one mid-match — the
   * player in that seat is the same human).
   */
  private handlePadConnect(index: number, id: string, label: string): void {
    let target = this.findAwaySeat(index, id, 'idAndPort');
    if (!target) target = this.findAwaySeat(index, id, 'id');
    if (!target) target = this.findAwaySeat(index, id, 'port');

    if (target) {
      target.slot = index;
      target.padId = id;
      target.label = label;
      target.connected = true;
      target.source = this.sourceFor('gamepad', index);
    }
    this.notifyBindings();
  }

  private handlePadDisconnect(index: number): void {
    const s = this.findSeatBySource('gamepad', index);
    // The binding is kept on purpose: the seat stays "theirs" until the lobby
    // explicitly releases it.
    if (s) {
      s.connected = false;
      zeroAxes(s.axes);
    }
    this.notifyBindings();
  }

  private findAwaySeat(
    index: number,
    id: string,
    match: 'idAndPort' | 'id' | 'port',
  ): SeatSlot | null {
    for (let i = 0; i < this.seats.length; i++) {
      const s = this.seats[i];
      if (s.kind !== 'gamepad' || s.connected) continue;
      if (match === 'idAndPort' && s.padId === id && s.slot === index) return s;
      if (match === 'id' && !!id && s.padId === id) return s;
      if (match === 'port' && s.slot === index) return s;
    }
    return null;
  }

  /* --------------------------------------------------------------- misc */

  /** Frozen, cached source objects: one per pad index / keyboard scheme. */
  private sourceFor(kind: SourceKind, slot: number): InputEvent['source'] {
    if (kind === 'gamepad') {
      let s = this.padSources[slot];
      if (!s) {
        s = Object.freeze({ kind: 'gamepad' as const, index: slot });
        this.padSources[slot] = s;
      }
      return s;
    }
    let s = this.keySources[slot];
    if (!s) {
      s = Object.freeze({ kind: 'keyboard' as const, scheme: slot });
      this.keySources[slot] = s;
    }
    return s;
  }

  private notifyBindings(): void {
    this.opts.onBindingsChanged?.(this.bindings);
  }
}

/** Fresh, plain description of a binding's source for the UI layer. */
function describeSource(kind: SourceKind | null, slot: number): SeatBinding['source'] {
  if (kind === 'gamepad') return { kind: 'gamepad', index: slot };
  if (kind === 'keyboard') return { kind: 'keyboard', scheme: slot };
  return null;
}

/** Number of keyboard schemes available to the lobby's "add player" list. */
export const KEYBOARD_SCHEME_COUNT = SCHEME_COUNT;
